import {
  Badge,
  KitchenCountertop,
  ListingHistoryState,
  ListingState,
  ListingTag,
  MasterBedroomLocation,
} from '__generated__/athena/graphql';

import { centsToDollars } from '../../helpers/money';
import { toDwellingType } from '../dwellingType';
import type {
  GetMarketplacePropertyDetailsQuery,
  MarketplaceBuyDirectStatus,
  MarketplaceDisplayBadge,
  MarketplaceKitchenCountertop,
  MarketplaceListingHistoryState,
  MarketplaceListingState,
  MarketplacePrimaryBedroomLocation,
} from './__generated__/marketplace-property.zeus';

const displayBadge = {
  AVAILABLE_SOON: Badge.AVAILABLE_SOON,
  IN_CONTRACT: Badge.IN_CONTRACT,
  ON_THE_MARKET: Badge.ON_THE_MARKET,
  RECENTLY_SOLD: Badge.RECENTLY_SOLD,
  TEMPORARILY_UNAVAILABLE: Badge.TEMP_UNAVAILABLE,
  TRADITIONAL: Badge.TRADITIONAL,
  UNKNOWN: Badge.UNKNOWN,
} satisfies Record<MarketplaceDisplayBadge, Badge>;

const listingTags = {
  AVAILABLE: [ListingTag.BUY_DIRECT_ELIGIBLE],
  BACKUP_OFFER: [ListingTag.BUY_DIRECT_ELIGIBLE, ListingTag.BUY_DIRECT_VERBAL_AGREEMENT],
  IN_CONTRACT: [ListingTag.BUY_DIRECT_ELIGIBLE, ListingTag.BUY_DIRECT_SALE_PENDING],
  OFFER_DEADLINE: [ListingTag.BUY_DIRECT_ELIGIBLE, ListingTag.BUY_DIRECT_MULTIPLE_OFFERS],
} satisfies Record<MarketplaceBuyDirectStatus, ListingTag[]>;

const kitchenCountertop = {
  CORIAN: KitchenCountertop.CORIAN,
  FORMICA: KitchenCountertop.FORMICA,
  GRANITE_SLAB: KitchenCountertop.GRANITE_SLAB,
  GRANITE_TILE: KitchenCountertop.GRANITE_TILE,
  OTHER_TILE: KitchenCountertop.OTHER_TILE,
  QUARTZ: KitchenCountertop.QUARTZ,
  UNKNOWN: KitchenCountertop.UNKNOWN,
} satisfies Record<MarketplaceKitchenCountertop, KitchenCountertop>;

const listingHistoryState = {
  COMING_SOON: ListingHistoryState.COMING_SOON,
  FOR_SALE: ListingHistoryState.FOR_SALE,
  OFF_MARKET: ListingHistoryState.OFF_MARKET,
  SALE_PENDING: ListingHistoryState.SALE_PENDING,
  SOLD: ListingHistoryState.SOLD,
  UNKNOWN: ListingHistoryState.UNKNOWN,
  WITHDRAWN: ListingHistoryState.WITHDRAWN,
} satisfies Record<MarketplaceListingHistoryState, ListingHistoryState>;

const listingState = {
  AVAILABLE_SOON: ListingState.AVAILABLE_SOON,
  IN_CONTRACT: ListingState.IN_CONTRACT,
  ON_THE_MARKET: ListingState.ON_THE_MARKET,
  RECENTLY_SOLD: ListingState.RECENTLY_SOLD,
  UNKNOWN: ListingState.UNKNOWN,
} satisfies Record<MarketplaceListingState, ListingState>;

const primaryBedroomLocation = {
  BASEMENT: MasterBedroomLocation.BASEMENT,
  FIRST_FLOOR: MasterBedroomLocation.FIRST_FLOOR,
  SECOND_FLOOR: MasterBedroomLocation.SECOND_FLOOR,
  THIRD_FLOOR: MasterBedroomLocation.THIRD_FLOOR,
  UNKNOWN: MasterBedroomLocation.UNKNOWN,
} satisfies Record<MarketplacePrimaryBedroomLocation, MasterBedroomLocation>;

export const mapMarketplaceProperty = (data: GetMarketplacePropertyDetailsQuery) => {
  const details = data.marketplace.listingDetails;
  if (!details) {
    return null;
  }
  const listing = details.summary;
  const availability = details.availability;
  if (!listing.slug) {
    return null;
  }

  const summaryListingState = listingState[listing.listingState];
  const currentListingState = availability ? listingState[availability.state] : null;
  const hasKnownCurrentAvailability =
    currentListingState != null && currentListingState !== ListingState.UNKNOWN;
  if (
    (!hasKnownCurrentAvailability && summaryListingState === ListingState.UNKNOWN) ||
    (currentListingState === ListingState.UNKNOWN && listing.displayBadge !== 'TRADITIONAL')
  ) {
    return null;
  }
  const resolvedListingState = hasKnownCurrentAvailability
    ? currentListingState
    : summaryListingState;

  return {
    addressToken: listing.addressId,
    annualPropertyTax: centsToDollars(details.annualPropertyTaxCents),
    bathrooms: listing.bathrooms,
    bedrooms: listing.bedrooms,
    buyDirectPrice: centsToDollars(details.buyDirect?.priceCents),
    buyDirectSavings: centsToDollars(details.buyDirect?.savingsCents),
    canSelfTour:
      hasKnownCurrentAvailability && availability ? availability.canSelfTour : listing.canSelfTour,
    city: listing.city,
    daysOnMarket: details.daysOnMarket,
    description: details.description,
    displayBadge:
      displayBadge[
        hasKnownCurrentAvailability && availability
          ? availability.displayBadge
          : listing.displayBadge
      ],
    dwellingType: toDwellingType(listing.dwellingType),
    estimatedMonthlyPayment: centsToDollars(listing.estimatedMonthlyPaymentCents),
    exteriorStories: details.exteriorStories,
    floorPlanPhotos: details.floorPlanPhotos.map((photo) => ({
      label: photo.label,
      url: photo.url,
    })),
    garageSpaces: details.garageSpaces,
    hasCurrentAvailability: availability != null,
    hasKnownCurrentAvailability,
    hasSellerDisclosures: details.hasSellerDisclosures,
    tourBookingListingId: details.tourBookingListingId,
    hoaMonthlyDues: centsToDollars(details.hoaMonthlyDuesCents),
    kitchenCountertop:
      details.kitchenCountertop == null ? null : kitchenCountertop[details.kitchenCountertop],
    latitude: listing.latitude,
    listPrice: centsToDollars(listing.listPriceCents),
    listingHistory: (details.listingHistory ?? []).map((event) => ({
      currentListPriceCents: event.currentListPriceCents,
      mls: event.mls,
      originalListPriceCents: event.originalListPriceCents,
      soldPriceCents: event.soldPriceCents,
      state: listingHistoryState[event.state],
      timestamp: event.timestamp,
    })),
    listingState: resolvedListingState,
    listingTags: details.buyDirect ? listingTags[details.buyDirect.status] : [],
    longitude: listing.longitude,
    masterBedroomLocation:
      details.primaryBedroomLocation == null
        ? null
        : primaryBedroomLocation[details.primaryBedroomLocation],
    photos: details.photos.map((photo) => photo.mediumUrl),
    photosLg: details.photos.map((photo) => photo.largeUrl),
    photosSm: details.photos.map((photo) => photo.smallUrl),
    photosXl: details.photos.map((photo) => photo.extraLargeUrl),
    photosXs: details.photos.map((photo) => photo.extraSmallUrl),
    pricePerSqFt: centsToDollars(listing.pricePerSquareFootCents),
    schoolDistricts: details.schoolDistricts ?? [],
    schools: (details.schools ?? []).map((school) => ({
      id: school.id,
      name: school.name,
      rating: school.rating,
    })),
    shouldShowGuarantee: details.shouldShowGuarantee,
    similarProperties: (details.similarListings ?? []).map((similar) => ({
      addressToken: similar.addressId,
      baths: similar.bathrooms,
      beds: similar.bedrooms,
      canSelfTour: similar.canSelfTour,
      city: similar.city,
      dwellingType: toDwellingType(similar.dwellingType),
      fullAddress: similar.fullAddress,
      listPrice: centsToDollars(similar.listPriceCents),
      livingSquareFootage: similar.totalLivingSquareFootage,
      neighborhood: similar.neighborhood,
      photos: similar.photoUrls,
      slug: similar.slug,
    })),
    slug: listing.slug,
    sqFtLot: listing.lotSquareFootage,
    sqFtTotalLiving: listing.totalLivingSquareFootage,
    state: listing.state,
    street: listing.street,
    unitNumber: details.unitNumber,
    unitType: details.unitType,
    walkthroughVideos: (details.walkthroughVideos ?? []).map((video) => ({
      id: video.id,
      url: video.url,
    })),
    yearBuilt: listing.yearBuilt == null ? null : String(listing.yearBuilt),
    zip: listing.postalCode,
  };
};
